import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const SearchBar = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  const suggestions = [
    { id: 1, name: "BPC-157", category: "Recovery & Healing", icon: "Heart" },
    { id: 2, name: "TB-500", category: "Performance Enhancement", icon: "Zap" },
    { id: 4, name: "CJC-1295", category: "Performance Enhancement", icon: "Zap" },
    { id: 5, name: "Sermorelin", category: "Anti-Aging", icon: "Sparkles" },
    { id: 6, name: "Thymosin Beta-4", category: "Recovery & Healing", icon: "Heart" },
    { id: 8, name: "Semax", category: "Cognitive Enhancement", icon: "Brain" }
  ];

  const filteredSuggestions = searchQuery?.length > 0
    ? suggestions?.filter(item =>
        item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/product-catalog?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setShowSuggestions(false);
      setIsFocused(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(`/product-detail-page?id=${suggestion?.id}`);
    setSearchQuery('');
    setShowSuggestions(false);
    setIsFocused(false);
  };

  const handleInputChange = (e) => {
    setSearchQuery(e?.target?.value);
    setShowSuggestions(e?.target?.value?.length > 0);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (searchQuery?.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <section className="w-full py-8 md:py-12 lg:py-16 bg-gradient-to-b from-[var(--color-background)] to-[var(--color-card)]">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Search Container */}
        <div className="max-w-3xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-[var(--color-foreground)] mb-2 md:mb-3">
              Find Your Perfect <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent">Peptide</span>
            </h2>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)]">
              Search from our extensive collection of premium peptides
            </p>
          </div>

          {/* Search Bar */}
          <div ref={searchRef} className="relative">
            <form onSubmit={handleSearch}>
              <div className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${
                isFocused 
                  ? 'shadow-[0_0_40px_rgba(0,212,255,0.3)] border-2 border-[var(--color-primary)]' 
                  : 'shadow-lg border-2 border-[rgba(0,212,255,0.2)]'
              }`}>
                
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-[rgba(26,27,35,0.95)] to-[rgba(42,45,58,0.95)] backdrop-blur-xl"></div>

                {/* Input Container */}
                <div className="relative flex items-center">
                  
                  {/* Search Icon */}
                  <div className="absolute left-4 md:left-6 pointer-events-none">
                    <Icon 
                      name="Search" 
                      size={20} 
                      color={isFocused ? "var(--color-primary)" : "var(--color-muted-foreground)"} 
                    />
                  </div>

                  {/* Input Field */}
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder="Search by peptide name or category..."
                    className="w-full h-14 md:h-16 pl-12 md:pl-16 pr-28 md:pr-32 bg-transparent text-[var(--color-foreground)] text-base md:text-lg placeholder:text-[var(--color-muted-foreground)] focus:outline-none"
                  />

                  {/* Search Button */}
                  <button
                    type="submit"
                    className="absolute right-2 md:right-3 px-4 md:px-6 py-2 md:py-3 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-[var(--color-primary-foreground)] font-semibold text-sm md:text-base transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,212,255,0.5)] hover:scale-105 flex items-center gap-2"
                  >
                    <span className="hidden sm:inline">Search</span>
                    <Icon name="ArrowRight" size={18} />
                  </button>
                </div>

                {/* Animated Border */}
                {isFocused && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[var(--color-primary)] via-[var(--color-secondary)] to-[var(--color-primary)] opacity-50 blur-sm animate-pulse"></div>
                  </div>
                )}
              </div>
            </form>

            {/* Suggestions Dropdown */}
            {showSuggestions && filteredSuggestions?.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 md:mt-3 rounded-2xl overflow-hidden border border-[rgba(0,212,255,0.3)] bg-[rgba(26,27,35,0.98)] backdrop-blur-xl shadow-2xl z-50 max-h-[400px] overflow-y-auto">
                {filteredSuggestions?.map((suggestion) => (
                  <div
                    key={suggestion?.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="flex items-center gap-4 px-4 md:px-6 py-3 md:py-4 cursor-pointer transition-all duration-200 hover:bg-[rgba(0,212,255,0.1)] border-b border-[rgba(0,212,255,0.1)] last:border-b-0"
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center">
                      <Icon name={suggestion?.icon} size={20} color="#FFFFFF" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm md:text-base font-semibold text-[var(--color-foreground)] truncate">
                        {suggestion?.name}
                      </p>
                      <p className="text-xs md:text-sm text-[var(--color-muted-foreground)] truncate">
                        {suggestion?.category}
                      </p>
                    </div>

                    {/* Arrow */}
                    <div className="flex-shrink-0">
                      <Icon name="ChevronRight" size={18} color="var(--color-primary)" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Results */}
            {showSuggestions && searchQuery?.length > 0 && filteredSuggestions?.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 md:mt-3 rounded-2xl overflow-hidden border border-[rgba(0,212,255,0.3)] bg-[rgba(26,27,35,0.98)] backdrop-blur-xl shadow-2xl z-50">
                <div className="px-4 md:px-6 py-6 md:py-8 text-center">
                  <Icon name="Search" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                  <p className="text-base md:text-lg font-medium text-[var(--color-foreground)] mb-2">
                    No results found
                  </p>
                  <p className="text-sm text-[var(--color-muted-foreground)]">
                    Try searching with different keywords
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Popular Searches */}
          <div className="mt-6 md:mt-8 text-center">
            <p className="text-sm text-[var(--color-muted-foreground)] mb-3">Popular searches:</p>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {['BPC-157', 'TB-500', 'Ipamorelin', 'CJC-1295']?.map((term) => (
                <button
                  key={term}
                  onClick={() => {
                    setSearchQuery(term);
                    setShowSuggestions(true);
                  }}
                  className="px-3 md:px-4 py-1.5 md:py-2 rounded-lg bg-[rgba(0,212,255,0.1)] border border-[rgba(0,212,255,0.2)] text-xs md:text-sm font-medium text-[var(--color-primary)] transition-all duration-200 hover:bg-[rgba(0,212,255,0.2)] hover:border-[rgba(0,212,255,0.4)]"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchBar;